import _ from 'underscore';
import Polyglot from 'node-polyglot';

const fixPluralization = (translation) => {
    // Pluralized translations contain a pipe character
    if (translation.indexOf('|') === -1) {
        return translation;
    }

    // Loop over each translation separated by pipe character and fix them
    const translations = translation.split('|').map(translation => {
        // Replace the 'id: translation' part with just 'translation'
        return translation.trim().replace(/^[a-z]+: (.+)$/, '$1');
    });

    // Join the translations with polyglot's |||| separator
    return translations.join(' |||| ');
};

/**
 * Locales are complicated. In our platform, however, we only support simple
 * locales that either contain a two-letter primary language code, for example
 * `en`, or a two-letter primary language code, an underscore, and a two-letter
 * country code, for example `en_US`. However, we only have translations for
 * primary languages, not for languages as spoken in specific regions, so we
 * only have to deal with locales with and without underscores, which is
 * trivial.
 *
 * @param locale {string}
 * @returns {string}
 */
const resolvePrimaryLanguage = (locale) => {
    return locale.includes('_')
        ? locale.substring(0, locale.indexOf('_'))
        : locale;

};

class Translator {
    constructor(defaultLanguage = 'en', translations = {}) {
        this.defaultLanguage = defaultLanguage;
        this.translations = translations;

        this.polyglot = new Polyglot({
            locale: this.defaultLanguage,
            warn: () => {
            },
        });
    }

    hasLanguage(language) {
        return !!this.translations[language];
    }

    getLanguage() {
        return resolvePrimaryLanguage(this.getLocale());
    }

    getLocale() {
        return this.polyglot.locale();
    }

    setLocale(locale, refresh) {
        if (!refresh && this.polyglot.locale() === locale) {
            return;
        }

        this.polyglot.locale(locale);

        const messages = this.getMessages(locale);

        this.polyglot.replace(messages);
    }

    getTranslations() {
        return this.translations;
    }

    getMessages(locale) {
        const language = resolvePrimaryLanguage(locale);

        return this.translations[language] ?? this.translations[this.defaultLanguage];
    }

    addTranslations(translations) {
        for (const [language, messages] of Object.entries(translations)) {
            this.addMessages(language, messages);
        }
    }

    addMessages(language, messages) {
        if (!this.translations[language]) {
            this.translations[language] = {};
        }

        for (const [key, translation] of Object.entries(messages)) {
            this.translations[language][key] = fixPluralization(translation);
        }

        // Update Polyglot messages if adding to the current language.
        if (language === this.getLanguage()) {
            this.polyglot.replace(this.translations[language]);
        }
    }

    translate(key, options) {
        options = options || {};

        // Handle polyglot pluralization shortcut
        if (_.isNumber(options)) {
            options = {
                count: options,
                smart_count: options,
            };
        }

        return this.polyglot.t(key, options);
    }
}

export const translator = new Translator();

window.Translator = translator;
